import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["valueInput", "displayButton", "displayText", "optionsPanel"]

  connect() {
    // Close dropdown when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener('click', this.boundClickOutside)

    this.boundSyncOptionsPanelHeight = this.syncOptionsPanelHeight.bind(this)
    window.addEventListener('resize', this.boundSyncOptionsPanelHeight)
    window.addEventListener('scroll', this.boundSyncOptionsPanelHeight, true)
  }

  disconnect() {
    document.removeEventListener('click', this.boundClickOutside)
    window.removeEventListener('resize', this.boundSyncOptionsPanelHeight)
    window.removeEventListener('scroll', this.boundSyncOptionsPanelHeight, true)
  }

  toggle() {
    const shouldOpen = this.optionsPanelTarget.classList.contains("hidden")

    if (shouldOpen) {
      this.applyOptionsPanelMaxHeight()
    }

    this.optionsPanelTarget.classList.toggle('hidden', !shouldOpen)
  }

  selectOption(event) {
    const selectedValue = event.currentTarget.dataset.value
    const selectedDisplay = event.currentTarget.dataset.display

    // Update the single source of truth
    this.valueInputTarget.value = selectedValue
    
    // Update display
    this.displayTextTarget.textContent = selectedDisplay
    
    // Hide options
    this.optionsPanelTarget.classList.add('hidden')
    
    // Dispatch change event so parent controllers can react
    this.valueInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    
    // Trigger confirm controller's change detection for form modification tracking
    this.triggerConfirmControllerChange()
  }

  // Handle clicks outside to close dropdown
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.optionsPanelTarget.classList.add('hidden')
    }
  }

  syncOptionsPanelHeight() {
    if (this.optionsPanelTarget.classList.contains('hidden')) return
    this.applyOptionsPanelMaxHeight()
  }

  applyOptionsPanelMaxHeight() {
    const panel = this.optionsPanelTarget
    const wrapper = this.displayButtonTarget.closest('.input__dropdown__wrapper')
    if (!panel || !wrapper) return

    const cardContainer = this.element.closest('.management__contain')
    const footer = document.querySelector('.footer')

    const wrapperRect = wrapper.getBoundingClientRect()
    const cardBottom = cardContainer ? cardContainer.getBoundingClientRect().bottom : window.innerHeight
    const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight
    const viewportBottom = window.innerHeight

    const effectiveBottom = Math.min(cardBottom, footerTop, viewportBottom)
    const availableHeight = Math.floor(effectiveBottom - wrapperRect.bottom - 8)

    const clampedHeight = Math.max(0, Math.min(302, availableHeight))
    panel.style.setProperty('--dropdown-max-height', `${clampedHeight}px`)
  }

  // Trigger confirm controller's change detection
  triggerConfirmControllerChange() {
    try {
      // Find the nearest confirm controller
      let element = this.element
      while (element && element !== document.body) {
        const confirmController = this.application.getControllerForElementAndIdentifier(element, 'confirm')
        if (confirmController && confirmController.onInputChange) {
          confirmController.onInputChange()
          break
        }
        element = element.parentElement
      }
    } catch (error) {
      // Silently fail if confirm controller is not found
      console.debug('Confirm controller not found for change detection')
    }
  }

  // Public method for programmatic updates
  updateValue(value, displayText) {
    this.valueInputTarget.value = value || ''
    this.displayTextTarget.textContent = displayText || this.getDefaultPlaceholder()
    
    // Dispatch change event for integration
    this.valueInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    
    // Trigger confirm controller's change detection for form modification tracking
    this.triggerConfirmControllerChange()
  }

  // Public method to clear selection
  clearSelection() {
    this.updateValue('', this.getDefaultPlaceholder())
  }

  // Public method to update options dynamically
  updateOptions(options) {
    // Clear existing options
    this.optionsPanelTarget.innerHTML = ''
    
    if (!options || options.length === 0) {
      const noOptionsDiv = document.createElement('div')
      noOptionsDiv.className = 'dropdown-option disabled'
      noOptionsDiv.textContent = 'No options available'
      this.optionsPanelTarget.appendChild(noOptionsDiv)
      
      // Clear current selection
      this.clearSelection()
    } else {
      options.forEach(option => {
        const optionDiv = document.createElement('div')
        optionDiv.className = 'dropdown-option'
        optionDiv.setAttribute('data-action', 'click->simplified-dropdown#selectOption')
        optionDiv.setAttribute('data-value', option.value || option.id)
        optionDiv.setAttribute('data-display', option.name || option.display)
        optionDiv.textContent = option.name || option.display
        this.optionsPanelTarget.appendChild(optionDiv)
      })
    }

    this.syncOptionsPanelHeight()
  }

  // Get default placeholder text
  getDefaultPlaceholder() {
    const fieldName = this.element.id
    
    switch (fieldName) {
      case 'device_type_id': return 'Select device type'
      case 'device_model_id': return 'Select model'
      case 'placement': return 'Select placement'
      case 'power_supply_id': return 'Select power supply'
      default: return 'Select value'
    }
  }

  // Public getter for current value
  get value() {
    return this.valueInputTarget.value
  }

  // Public getter for current display text
  get displayText() {
    return this.displayTextTarget.textContent
  }

  // Public method to check if dropdown has options
  get hasOptions() {
    const options = this.optionsPanelTarget.querySelectorAll('.dropdown-option:not(.disabled)')
    return options.length > 0
  }
}
